import { useEffect } from 'react';
import { type IDBPDatabase, openDB } from 'idb';
import { enableMapSet } from 'immer';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Artifact } from '~api';
import { api } from '~portal/api';
import { logger } from '~portal/lib';

// Enable Immer support for Map and Set
enableMapSet();

type SignedUrlCache = {
  url: string;
  expiresAt: Date;
  // Absent on entries written before the format was cached (H2-206): a stale IndexedDB record is
  // still a usable URL, so it is read as unknown rather than discarded.
  format?: Artifact.Format;
  // The producer's basename (ADR-21). Absent both on stale cache entries and on artefacts stored
  // before names existed, so every reader falls back to the id-based label.
  name?: string;
};

type ArtifactState = {
  signedUrls: Map<string, SignedUrlCache>;
  setSignedUrl: (
    artifactId: string,
    url: string,
    expiresAt: Date,
    format: Artifact.Format,
    name?: string,
  ) => void;
  getSignedUrl: (artifactId: string) => SignedUrlCache | undefined;
};

const DB_NAME = 'artifact-cache';
const STORE_NAME = 'signed-urls';
const DB_VERSION = 1;

type SignedUrlRecord = {
  artifactId: string;
  url: string;
  expiresAt: number;
  format?: Artifact.Format;
  name?: string;
};

type ArtifactDB = {
  [STORE_NAME]: {
    key: string;
    value: SignedUrlRecord;
    indexes: { expiresAt: number };
  };
};

/**
 * @desc Initialize IndexedDB connection with schema
 */
async function initDB(): Promise<IDBPDatabase<ArtifactDB>> {
  return openDB<ArtifactDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'artifactId' });
        store.createIndex('expiresAt', 'expiresAt');
      }
    },
  });
}

/**
 * @desc Load cached signed URLs from IndexedDB, filtering expired entries
 */
async function loadFromIndexedDB(): Promise<Map<string, SignedUrlCache>> {
  try {
    const db = await initDB();
    const now = Date.now();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const allRecords = await store.getAll();
    const validEntries: Array<[string, SignedUrlCache]> = [];

    // Filter out expired entries and delete them
    for (const record of allRecords) {
      if (record.expiresAt > now) {
        validEntries.push([
          record.artifactId,
          {
            url: record.url,
            expiresAt: new Date(record.expiresAt),
            format: record.format,
            name: record.name,
          },
        ]);
      } else {
        // Delete expired entry
        await store.delete(record.artifactId);
      }
    }

    await tx.done;
    db.close();

    return new Map(validEntries);
  } catch (err) {
    logger.error('[ArtifactStore] Failed to load artifact cache from IndexedDB', {
      error: err instanceof Error ? err.message : String(err),
    });
    return new Map();
  }
}

/**
 * @desc Save a signed URL to IndexedDB
 */
async function saveToIndexedDB(
  artifactId: string,
  url: string,
  expiresAt: Date,
  format: Artifact.Format,
  name?: string,
) {
  const db = await initDB();
  try {
    await db.put(STORE_NAME, {
      artifactId,
      url,
      expiresAt: expiresAt.getTime(),
      format,
      name,
    });
  } finally {
    db.close();
  }
}

/**
 * @desc Delete a signed URL from IndexedDB
 */
async function deleteFromIndexedDB(artifactId: string) {
  const db = await initDB();
  try {
    await db.delete(STORE_NAME, artifactId);
  } finally {
    db.close();
  }
}

// Initialize store with empty Map, load from IndexedDB asynchronously
let storeInitialized = false;

export const useArtifactStore = create(
  immer<ArtifactState>((set, get) => ({
    signedUrls: new Map(),

    setSignedUrl: (
      artifactId: string,
      url: string,
      expiresAt: Date,
      format: Artifact.Format,
      name?: string,
    ) => {
      set((state) => {
        state.signedUrls.set(artifactId, { url, expiresAt, format, name });
      });
      // Persist to IndexedDB asynchronously (non-blocking)
      saveToIndexedDB(artifactId, url, expiresAt, format, name).catch((err) => {
        logger.error('[ArtifactStore] Failed to save to IndexedDB', {
          artifactId,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    },

    getSignedUrl: (artifactId: string) => {
      const cached = get().signedUrls.get(artifactId);
      if (!cached) return undefined;

      // Check if expired
      if (cached.expiresAt.getTime() <= Date.now()) {
        // Remove expired entry
        set((state) => {
          state.signedUrls.delete(artifactId);
        });
        // Delete from IndexedDB asynchronously
        deleteFromIndexedDB(artifactId).catch((err) => {
          logger.error('[ArtifactStore] Failed to delete from IndexedDB', {
            artifactId,
            error: err instanceof Error ? err.message : String(err),
          });
        });
        return undefined;
      }

      return cached;
    },
  })),
);

/**
 * @desc Initialize store by loading cached data from IndexedDB
 */
async function initializeStore() {
  if (storeInitialized) return;
  storeInitialized = true;

  const cached = await loadFromIndexedDB();
  useArtifactStore.setState({ signedUrls: cached });
}

// Load cache on module initialization
initializeStore().catch((err) => {
  logger.error('[ArtifactStore] Failed to initialize store', {
    error: err instanceof Error ? err.message : String(err),
  });
});

/**
 * @desc Hook to fetch signed URL for an artifact with IndexedDB caching
 */
export function useArtifactSignedUrl(artifactId: string | undefined) {
  const { getSignedUrl, setSignedUrl } = useArtifactStore();

  // Check IndexedDB cache first
  const cached = artifactId ? getSignedUrl(artifactId) : undefined;

  // Only fetch from API if not in cache
  const query = api.artifacts.getSignedUrl.useQuery(
    { artifactId: artifactId! },
    {
      enabled: !!artifactId && !cached,
      retry: 1,
    },
  );

  // Store in cache when fetched
  useEffect(() => {
    if (query.data && artifactId) {
      setSignedUrl(
        artifactId,
        query.data.url,
        new Date(query.data.expiresAt),
        query.data.format,
        query.data.name,
      );
    }
  }, [query.data, artifactId, setSignedUrl]);

  return {
    url: cached?.url ?? query.data?.url ?? null,
    // The artefact's stored format, which an inline `artifact:<id>` reference does not carry —
    // it is what decides between an inline image and a download card (H2-206).
    format: cached?.format ?? query.data?.format ?? null,
    // The producer's basename, when the backend knows one — the only source of a file name for a
    // reference to an artefact this conversation's transcript does not contain (ADR-21).
    name: cached?.name ?? query.data?.name ?? null,
    isLoading: !cached && query.isLoading,
    error: query.error,
  };
}
